import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  BmaAnnahme, BmaBefund, BmaFachfrage, BmaKapitel, BmaKomponente, BmaLoopplan, BmaPruefung, BmaStand, BmaUnterlage,
  BmaWirkprinzip, BmaZentrale,
} from './elektro/bma';
import {
  LOOPPLAN_AKTUELL, LOOPPLAN_FEHLT, PDF_HINWEIS, alsVorbehalte, auswahlParameter, einzelausgabe, rechenstand,
  unterlagenabruf, unterlagenzustand,
} from './elektro/bma';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, ExportLeiste, Feld, KpiCard, KpiReihe,
  LoadingState, StatusBadge, Vorbehalte, type ExportZiel, type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Brandmeldeanlage (BM-1, 20.10.2026) - Einstieg des Abschnitts „Brandmeldeanlage" der Elektro-Gliederung.
 *
 * Die Seite LIEST `GET …/elektro/bma`: Kennzahlen, Vorbehalte, die Kapitel des Hefts ELT-BMA, Zentralen,
 * Komponenten, Annahmen, Fachfragen und Befunde. Gerechnet wird auf dem Server (Bmastand), die Seite schreibt
 * nichts. Die Vorbehalte stehen wortgleich im Vorbehaltskasten des Hefts.
 *
 * Ausgaben (TX-STANDARD S1.9):
 *   - PDF Gesamtbericht: Heft ELT-BMA mit allen Kapiteln;
 *   - Kapitel wählen …: ein Schubfach mit Haken je Kapitel, daraus ein Teilheft (dieselbe Art, der Gegenstand
 *     nennt die Auswahl); je Kapitel auch einzeln in der Kapiteltabelle. Kapitel mit eigener Berichtsart (ELT-BFS,
 *     ELT-BNW, ELT-MLD) liefern diese Art.
 *   - Excel: BMA-Listen über den Excel-Kern.
 *   - Loopläne (BM-5): ELT-LOP je Zentrale mit Nummer und Zustand wie Kapitel 9, Abruf je Zentrale.
 *   - Prüfung und Inbetriebsetzung (BM-7): Unterlagen mit Zustand wie Kapitel 14, Abruf je Berichtsart, dazu die
 *     Wirkprinzipprüfung je Ansteuerung (volle Liste im Blatt „Wirkprinzipprüfung“ der BMA-Listen).
 */
export function ElektroBma() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <BmaAnsicht api={api} projektId={projekt.id} />;
}

function rangZustand(rang: string): Zustand {
  return rang === 'Fehler' ? 'fehler' : rang === 'Warnung' ? 'warnung' : 'info';
}

export function BmaAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<BmaStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schubfach, setSchubfach] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [einzelFehler, setEinzelFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const s = await api.elektroBma(projektId);
      setStand(s);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const waehlbar = useMemo(() => (stand?.kapitel ?? []).filter((k) => k.art === 'wahl'), [stand]);
  const parameter = stand ? auswahlParameter(gewaehlt, stand.kapitel) : '';

  async function kapitelLaden(k: BmaKapitel) {
    setEinzelFehler(null);
    try {
      switch (einzelausgabe(k)) {
        case 'bfs': await api.elektroSicherheitstechnikMatrixLaden(projektId); break;
        case 'bnw': await api.elektroSicherheitstechnikNachweisLaden(projektId); break;
        case 'mld': await api.elektroMelderlisteLaden(projektId); break;
        case 'teilheft': await api.elektroBmaBerichtLaden(projektId, k.nummer); break;
        default: break;
      }
    } catch (e: unknown) {
      setEinzelFehler(e instanceof Error ? e.message : String(e));
    }
  }

  const ziele: ExportZiel[] = [
    { art: 'pdf', beschriftung: 'PDF Gesamtbericht', laden: () => api.elektroBmaBerichtLaden(projektId), hinweis: PDF_HINWEIS },
    { art: 'excel', beschriftung: 'Excel BMA-Listen', laden: () => api.elektroBmaListen(projektId) },
  ];

  const aktionen = (
    <>
      <ExportLeiste ziele={ziele} />
      <button type="button" className="m-knopf" onClick={() => setSchubfach(true)} disabled={!stand}>
        Kapitel wählen …
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Brandmeldeanlage"
        beschreibung="Zentralen, Komponenten, Melderbedarf, Meldergruppen, Ringe, Steuermatrix, Notstrom und Schnittstellen der BMA aus einem Bestand – mit Gesamtbericht und Kapitelwahl als PDF. Nur lesend."
        meta={stand ? rechenstand(stand.staende) : 'noch nicht geladen'}
        aktionen={aktionen}
      />
      {fehler && (
        <ErrorState titel="Der Stand der Brandmeldeanlage konnte nicht geladen werden." technisch={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      )}
      {!fehler && !stand && <LoadingState />}
      {stand && stand.kennzahlen.bmz === 0 && (
        <EmptyState titel="Keine Brandmelderzentrale angelegt"
                    erklaerung="Zentralen und Linien entstehen über „Zentralen, Meldergruppen und Ringe“ (Zentralen erkennen, Linien bilden). Danach steht hier die Brandmeldeanlage." />
      )}
      {stand && stand.kennzahlen.bmz > 0 && (
        <Inhalt stand={stand} projektId={projektId} beiKapitel={(k) => { void kapitelLaden(k); }}
                einzelFehler={einzelFehler} api={api} />
      )}
      <Drawer offen={schubfach} titel="Kapitel wählen" beiSchliessen={() => setSchubfach(false)}
              beschreibung="Das Teilheft enthält die gewählten Kapitel und immer die Pflichtkapitel 1 Grundlagen und Normbezug, 2 Annahmen und Vorgaben, 15 Befunde und 16 Offene Fachfragen."
              fuss={(
                <ExportLeiste ziele={[{
                  art: 'pdf', beschriftung: 'PDF der Auswahl', hinweis: PDF_HINWEIS,
                  laden: () => api.elektroBmaBerichtLaden(projektId, parameter),
                  gesperrt: parameter === '' ? { grund: 'Mindestens ein Kapitel wählen.' } : undefined,
                }]} />
              )}>
        <Feld art="mehrfachauswahl" beschriftung="Kapitel" breite="voll" wert={gewaehlt}
              optionen={waehlbar.map((k) => ({ wert: k.nummer, beschriftung: `${k.nummer} ${k.titel}` }))}
              beiAenderung={(w) => setGewaehlt(Array.isArray(w) ? (w as string[]) : [])}
              hinweis={parameter ? `Auswahl: ${parameter.split(',').join(', ')}` : 'Noch kein Kapitel gewählt.'} />
      </Drawer>
    </div>
  );
}

function Inhalt({ stand, projektId, beiKapitel, einzelFehler, api }: {
  stand: BmaStand; projektId: string; beiKapitel: (k: BmaKapitel) => void; einzelFehler: string | null;
  api: ApiClient;
}) {
  const k = stand.kennzahlen;
  return (
    <>
      <Vorbehalte eintraege={alsVorbehalte(stand.vorbehalte)} />
      <KpiReihe>
        <KpiCard titel="Brandmelderzentralen" wert={k.bmz} vergleich={`${k.ringe} Ringe, ${k.rwa} RWA-Zentralen`} />
        <KpiCard titel="Teilnehmer an BMA-Ringen" wert={k.teilnehmer} />
        <KpiCard titel="Meldergruppen" wert={k.gruppen} vergleich={`${k.abschnitte} Abschnitte (abgeleitet)`} />
        <KpiCard titel="Räume mit fehlenden Meldern" wert={k.raeumeFehlend}
                 vergleich={`${k.melderErforderlich} erforderlich, ${k.melderVorhanden} vorhanden`}
                 zustand={k.raeumeFehlend > 0 ? 'warnung' : 'gut'} zustandText={k.raeumeFehlend > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Ringe mit Befund" wert={k.ringeMitBefund} vergleich={`von ${k.ringe}`}
                 zustand={k.ringeMitBefund > 0 ? 'warnung' : 'gut'} zustandText={k.ringeMitBefund > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Befunde" wert={k.warnungen + k.fehler} vergleich={`${k.fehler} Fehler, ${k.warnungen} Warnungen`}
                 zustand={k.fehler > 0 ? 'fehler' : k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.fehler > 0 ? 'beheben' : k.warnungen > 0 ? 'prüfen' : 'keine'} />
      </KpiReihe>
      <Kapitel kapitel={stand.kapitel} projektId={projektId} beiKapitel={beiKapitel} fehler={einzelFehler} />
      <Zentralen zeilen={stand.zentralen} entscheidung={stand.kapazitaet.entscheidung} />
      <Loopplaene zeilen={stand.loopplaene ?? []} api={api} />
      {stand.pruefung && <Pruefung pruefung={stand.pruefung} api={api} projektId={projektId} />}
      <Komponenten zeilen={stand.komponenten} />
      <Annahmen zeilen={stand.annahmen} />
      <Fachfragen zeilen={stand.fachfragen} />
      <Befunde zeilen={stand.befunde} />
    </>
  );
}

function Kapitel({ kapitel, projektId, beiKapitel, fehler }: {
  kapitel: BmaKapitel[]; projektId: string; beiKapitel: (k: BmaKapitel) => void; fehler: string | null;
}) {
  const spalten: Spalte<BmaKapitel>[] = [
    { schluessel: 'nummer', titel: 'Nr.', wert: (z) => z.nummer, immerSichtbar: true },
    { schluessel: 'titel', titel: 'Kapitel', wert: (z) => z.titel, immerSichtbar: true },
    {
      schluessel: 'art', titel: 'Art', art: 'status', wert: (z) => z.art,
      inhalt: (z) => (z.art === 'pflicht' ? <StatusBadge zustand="neutral" text="Pflicht" klein />
        : z.art === 'gesamt' ? <StatusBadge zustand="neutral" text="nur Gesamtbericht" klein />
          : <StatusBadge zustand="info" text="wählbar" klein />),
    },
    { schluessel: 'einzel', titel: 'Einzelausgabe', wert: (z) => (z.art === 'wahl' ? (z.einzelausgabe ?? 'Teilheft ELT-BMA') : '—') },
    { schluessel: 'befunde', titel: 'Befunde', art: 'zahl', wert: (z) => z.befunde },
    { schluessel: 'quelle', titel: 'Quelle', wert: (z) => z.quelle, versteckt: true },
    {
      schluessel: 'seite', titel: 'Seite', wert: (z) => z.seite ?? '',
      inhalt: (z) => (z.seite ? <Link to={`/projekte/${projektId}/${z.seite}`}>öffnen</Link> : '—'),
    },
    {
      schluessel: 'pdf', titel: 'PDF', wert: () => '', sortierbar: false,
      inhalt: (z) => (z.art === 'wahl'
        ? (
          <button type="button" className="m-knopf" title={PDF_HINWEIS}
                  aria-label={`Kapitel ${z.nummer} ${z.titel} als PDF`} onClick={() => beiKapitel(z)}>
            PDF
          </button>
        )
        : '—'),
    },
  ];
  return (
    <ContentSection titel="Kapitel des Hefts ELT-BMA"
                    beschreibung="Jedes wählbare Kapitel einzeln als PDF: mit eigener Berichtsart deren Fassung, sonst ein Teilheft mit den Pflichtkapiteln. Mehrere Kapitel über „Kapitel wählen …“.">
      {fehler && <ErrorState titel="Das Kapitel konnte nicht erzeugt werden." technisch={fehler} klein />}
      <DataTable name={`Kapitel (${kapitel.length})`} spalten={spalten} zeilen={kapitel}
                 schluessel={(z) => z.nummer} suche={false} dicht />
    </ContentSection>
  );
}

function Zentralen({ zeilen, entscheidung }: { zeilen: BmaZentrale[]; entscheidung: string | null }) {
  const spalten: Spalte<BmaZentrale>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.kennzeichen, immerSichtbar: true },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art.toUpperCase() },
    { schluessel: 'baureihe', titel: 'Baureihe', wert: (z) => z.baureihe ?? '—' },
    { schluessel: 'linien', titel: 'Linien', art: 'zahl', wert: (z) => z.linien },
    { schluessel: 'teilnehmer', titel: 'Teilnehmer', art: 'zahl', wert: (z) => z.teilnehmer },
    { schluessel: 'max', titel: 'Ringe gepflegte Ausbaustufe', art: 'zahl', wert: (z) => z.maxRinge },
    { schluessel: 'steck', titel: 'Steckplätze', art: 'zahl', wert: (z) => z.steckplaetze },
    { schluessel: 'module', titel: 'Ringmodule', art: 'zahl', wert: (z) => z.ringmodule },
    {
      schluessel: 'einspeisung', titel: 'Netzeinspeisung', art: 'status', wert: (z) => z.einspeisung,
      inhalt: (z) => <StatusBadge zustand={z.einspeisung ? 'gut' : 'warnung'} text={z.einspeisung ? 'zugeordnet' : 'fehlt'} klein />,
    },
    { schluessel: 'bewertung', titel: 'Bewertung', wert: (z) => z.bewertung },
  ];
  return (
    <ContentSection titel="Zentralen" beschreibung={entscheidung ?? 'Belegung der Zentralen gegen die gepflegte Ausbaustufe der Baureihe.'}>
      <DataTable name={`Zentralen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.kennzeichen} suche={false} dicht
                 leer={<EmptyState titel="Keine Zentrale" erklaerung="Im Bestand steht keine BMZ oder RWA-Zentrale." />} />
    </ContentSection>
  );
}

function loopplanZustand(zustand: string): Zustand {
  return zustand === LOOPPLAN_AKTUELL ? 'gut' : zustand === LOOPPLAN_FEHLT ? 'neutral' : 'warnung';
}

/** BM-5: die Loopläne ELT-LOP je Zentrale - dasselbe Verzeichnis wie Kapitel 9 des Hefts, Abruf je Zentrale. */
function Loopplaene({ zeilen, api }: { zeilen: BmaLoopplan[]; api: ApiClient }) {
  const [fehler, setFehler] = useState<string | null>(null);
  async function laden(z: BmaLoopplan) {
    setFehler(null);
    try {
      await api.elektroLoopplanLaden(z.zentraleId, z.zentrale);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }
  const spalten: Spalte<BmaLoopplan>[] = [
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.zentrale, immerSichtbar: true },
    { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (z) => z.gegenstand },
    { schluessel: 'nummer', titel: 'Dokumentnummer', wert: (z) => z.dokumentnummer ?? '—' },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', wert: (z) => z.zustand,
      inhalt: (z) => <StatusBadge zustand={loopplanZustand(z.zustand)} text={z.zustand} klein />,
    },
    {
      schluessel: 'pdf', titel: 'PDF', wert: () => '', sortierbar: false,
      inhalt: (z) => (
        <button type="button" className="m-knopf" title={PDF_HINWEIS}
                aria-label={`Loopplan ${z.zentrale} als PDF`} onClick={() => { void laden(z); }}>
          PDF
        </button>
      ),
    },
  ];
  return (
    <ContentSection titel="Loopläne (ELT-LOP)"
                    beschreibung="Je Zentrale die jüngste Fassung im Register und ob sie zum heutigen Stand passt – wie Kapitel 9 des Hefts. Der Abruf erzeugt bei geändertem Stand eine neue Fassung, sonst liefert er die abgelegte.">
      {fehler && <ErrorState titel="Der Loopplan konnte nicht erzeugt werden." technisch={fehler} klein />}
      <DataTable name={`Loopläne (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.zentrale} suche={false} dicht
                 leer={<EmptyState titel="Keine Zentrale" erklaerung="Im Bestand steht keine BMZ oder RWA-Zentrale." />} />
    </ContentSection>
  );
}

/** BM-7: Prüfung und Inbetriebsetzung - dieselbe Unterlagenliste und Wirkprinzipprüfung wie Kapitel 14 des Hefts. */
function Pruefung({ pruefung, api, projektId }: { pruefung: BmaPruefung; api: ApiClient; projektId: string }) {
  const [fehler, setFehler] = useState<string | null>(null);
  async function laden(z: BmaUnterlage) {
    setFehler(null);
    try {
      switch (unterlagenabruf(z.art)) {
        case 'bfs': await api.elektroSicherheitstechnikMatrixLaden(projektId); break;
        case 'bnw': await api.elektroSicherheitstechnikNachweisLaden(projektId); break;
        case 'mld': await api.elektroMelderlisteLaden(projektId); break;
        case 'feh': await api.elektroFunktionserhaltPdfLaden(projektId); break;
        default: break;
      }
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }
  const spalten: Spalte<BmaUnterlage>[] = [
    { schluessel: 'name', titel: 'Unterlage', wert: (z) => z.name, immerSichtbar: true },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art ?? '—' },
    { schluessel: 'nummer', titel: 'Dokumentnummer', wert: (z) => z.dokumentnummer ?? '—' },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', wert: (z) => z.zustand,
      inhalt: (z) => <StatusBadge zustand={unterlagenzustand(z.zustand)} text={z.zustand} klein />,
    },
    { schluessel: 'quelle', titel: 'Quelle', wert: (z) => z.quelle, versteckt: true },
    {
      schluessel: 'pdf', titel: 'PDF', wert: () => '', sortierbar: false,
      inhalt: (z) => (unterlagenabruf(z.art)
        ? (
          <button type="button" className="m-knopf" title={PDF_HINWEIS}
                  aria-label={`${z.name} als PDF`} onClick={() => { void laden(z); }}>
            PDF
          </button>
        )
        : '—'),
    },
  ];
  const wirk: Spalte<BmaWirkprinzip>[] = [
    { schluessel: 'ansteuerung', titel: 'Ansteuerung', wert: (z) => z.ansteuerung, immerSichtbar: true },
    { schluessel: 'wirkung', titel: 'Wirkung', wert: (z) => z.wirkung },
    { schluessel: 'abschnitte', titel: 'Abschnitte', art: 'zahl', wert: (z) => z.abschnitte },
    { schluessel: 'objekte', titel: 'Objekte', art: 'zahl', wert: (z) => z.objekte },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => z.herkunft },
  ];
  return (
    <ContentSection titel="Prüfung und Inbetriebsetzung" beschreibung={pruefung.normsatz}>
      {fehler && <ErrorState titel="Die Unterlage konnte nicht erzeugt werden." technisch={fehler} klein />}
      <DataTable name={`Unterlagen (${pruefung.unterlagen.length})`} spalten={spalten} zeilen={pruefung.unterlagen}
                 schluessel={(z) => z.name} suche={false} dicht />
      <DataTable name={`Wirkprinzipprüfung: ${pruefung.pruefpunkte} Prüfpunkte je Abschnitt und Ansteuerung (Blatt „Wirkprinzipprüfung“ der BMA-Listen)`}
                 spalten={wirk} zeilen={pruefung.wirkprinzip} schluessel={(z) => z.ansteuerung} suche={false} dicht
                 leer={<EmptyState titel="Keine Ansteuerung" erklaerung="Die Steuermatrix führt noch keine Ansteuerung." />} />
    </ContentSection>
  );
}

function Komponenten({ zeilen }: { zeilen: BmaKomponente[] }) {
  const spalten: Spalte<BmaKomponente>[] = [
    { schluessel: 'system', titel: 'System', wert: (z) => z.system },
    { schluessel: 'komponente', titel: 'Komponente', wert: (z) => z.bezeichnung, immerSichtbar: true },
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ ?? '—' },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (z) => z.anzahl },
    { schluessel: 'adresse', titel: 'mit Adresse', art: 'zahl', wert: (z) => z.mitAdresse },
    { schluessel: 'ohneraum', titel: 'ohne Raum', art: 'zahl', wert: (z) => z.ohneRaum },
    { schluessel: 'datenblatt', titel: 'mit Datenblatt', art: 'zahl', wert: (z) => z.mitDatenblatt },
    {
      schluessel: 'besonderheit', titel: 'Besonderheit', wert: (z) => z.besonderheit ?? '',
      inhalt: (z) => (z.besonderheit ? <StatusBadge zustand="warnung" text={z.besonderheit} klein /> : ''),
    },
    { schluessel: 'norm', titel: 'Norm des Typs', wert: (z) => z.norm ?? '—' },
  ];
  return (
    <ContentSection titel="Komponenten nach Art"
                    beschreibung="Teilnehmer an Linien und Zentralen der BMA und RWA nach Typ. Sockelsirenen und RWA-Bedienstellen sind gekennzeichnet.">
      <DataTable name={`Komponenten (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.system}|${z.bauteilart}|${z.typ ?? ''}`} dicht ausfuhr
                 ausfuhrDateiname="bma-komponenten.csv"
                 leer={<EmptyState titel="Keine Komponenten" erklaerung="An den Zentralen der BMA hängt noch kein Teilnehmer." />} />
    </ContentSection>
  );
}

function Annahmen({ zeilen }: { zeilen: BmaAnnahme[] }) {
  const spalten: Spalte<BmaAnnahme>[] = [
    { schluessel: 'parameter', titel: 'Parameter', wert: (z) => z.parameter, immerSichtbar: true },
    { schluessel: 'wert', titel: 'Wert', wert: (z) => z.wert ?? '—' },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit ?? '' },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => z.herkunft },
    { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (z) => [z.quelle, z.normbezug].filter(Boolean).join(' · ') },
    {
      schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status', wert: (z) => z.bestaetigt,
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'bestätigt' : 'unbestätigt'} klein />,
    },
  ];
  const geordnet = [...zeilen].sort((a, b) => Number(a.bestaetigt) - Number(b.bestaetigt));
  return (
    <ContentSection titel="Annahmen und Vorgaben"
                    beschreibung="Dieselbe Liste wie das Annahmenblatt des Hefts (Kapitel 2) und das Blatt „Annahmen“ der BMA-Listen; unbestätigte zuerst.">
      <DataTable name={`Annahmen (${zeilen.length})`} spalten={spalten} zeilen={geordnet}
                 schluessel={(z) => z.parameter} dicht ausfuhr ausfuhrDateiname="bma-annahmen.csv"
                 leer={<EmptyState titel="Keine Annahme" erklaerung="Für dieses Projekt ist keine Vorgabe der BMA angelegt." />} />
    </ContentSection>
  );
}

function Fachfragen({ zeilen }: { zeilen: BmaFachfrage[] }) {
  const spalten: Spalte<BmaFachfrage>[] = [
    { schluessel: 'nummer', titel: 'Nr.', wert: (z) => z.nummer, immerSichtbar: true },
    { schluessel: 'prio', titel: 'Prio', wert: (z) => z.prio },
    {
      schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={z.antwort ? 'gut' : 'warnung'} text={z.antwort ? 'beantwortet' : 'offen'} klein />,
    },
    { schluessel: 'frage', titel: 'Frage', wert: (z) => z.frage },
    { schluessel: 'antwort', titel: 'Antwort', wert: (z) => z.antwort ?? '—' },
    { schluessel: 'kapitel', titel: 'Kapitel', wert: (z) => z.kapitel },
    { schluessel: 'quelle', titel: 'Quelle', wert: (z) => z.quelle, versteckt: true },
  ];
  return (
    <ContentSection titel="Fachfragen" beschreibung="Fachfragen mit Bezug zur BMA – offen und beantwortet, mit den Kapiteln, deren Ergebnis daran hängt.">
      <DataTable name={`Fachfragen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.nummer} dicht
                 leer={<EmptyState titel="Keine Fachfrage" erklaerung="Zur Brandmeldeanlage ist keine Fachfrage offen." />} />
    </ContentSection>
  );
}

function Befunde({ zeilen }: { zeilen: BmaBefund[] }) {
  const spalten: Spalte<BmaBefund>[] = [
    {
      schluessel: 'rang', titel: 'Rang', art: 'status', wert: (z) => z.rang,
      inhalt: (z) => <StatusBadge zustand={rangZustand(z.rang)} text={z.rang} klein />,
    },
    { schluessel: 'regel', titel: 'Regel', wert: (z) => z.regel },
    { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (z) => z.gegenstand },
    { schluessel: 'meldung', titel: 'Meldung', wert: (z) => z.meldung, immerSichtbar: true },
    { schluessel: 'kapitel', titel: 'Kapitel', wert: (z) => z.kapitel },
  ];
  return (
    <ContentSection titel="Befunde" beschreibung="Fehler zuerst, dann Warnungen und Hinweise – dieselbe Liste wie Kapitel 15 des Hefts.">
      <DataTable name={`Befunde (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.kapitel}|${z.regel}|${z.gegenstand}|${z.meldung}`} dicht ausfuhr
                 ausfuhrDateiname="bma-befunde.csv"
                 leer={<EmptyState titel="Keine Befunde" erklaerung="Der Stand der Brandmeldeanlage ergibt keinen Befund." />} />
    </ContentSection>
  );
}
